import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRightArrowLeft,
  faCircleCheck,
  faPlus,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { debounceTime } from 'rxjs';
import { NativeSpace, nativeBackendUrl } from '../../../../environments/backend-endpoints';
import { config } from '../../../../environments/environment';
import { TextField } from '../../../shared/components/form-fields/text-field/text-field';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import {
  addSpace,
  loadSpaces,
  parseSpaceAddress,
  removeSpace,
  selectSpace,
  Space,
} from '../../../shared/helpers/spaces';
import { TENANT_HEADER } from '../../../shared/helpers/tenant';
import { UserSettingsStore } from '../../../state/user-settings.repository';

type Lookup =
  | { state: 'idle' }
  | { state: 'loading'; domain: string }
  | { state: 'found'; space: Space }
  | { state: 'error'; message: string };

/**
 * Native app only: the spaces (tenants) the app knows, and the form to add
 * one. Shown as a bottom sheet from the switch button next to the logo, and
 * as the first screen while the app has no space at all (`required`).
 */
@Component({
  selector: 'andor-space-sheet',
  standalone: true,
  imports: [FontAwesomeModule, ReactiveFormsModule, TranslatePipe, TextField, Button],
  templateUrl: './space-sheet.html',
  styleUrl: './space-sheet.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpaceSheet {
  private readonly document = inject(DOCUMENT);
  private readonly userSettings = inject(UserSettingsStore);

  /** No space yet: the sheet can't be dismissed and opens on the form. */
  required = input(false);
  closed = output<void>();

  readonly faArrowRightArrowLeft = faArrowRightArrowLeft;
  readonly faCircleCheck = faCircleCheck;
  readonly faPlus = faPlus;
  readonly faTrashCan = faTrashCan;
  readonly faXmark = faXmark;

  readonly colorMode = toSignal(this.userSettings.colorMode$, { initialValue: null });
  readonly spaces = signal(loadSpaces());
  readonly adding = signal(loadSpaces().spaces.length === 0);
  readonly lookup = signal<Lookup>({ state: 'idle' });
  readonly address = new FormControl('', { nonNullable: true });

  private lookupRun = 0;

  constructor() {
    this.address.valueChanges
      .pipe(debounceTime(400), takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((value) => void this.lookUp(value));
  }

  logo(space: Space): string | null {
    return (this.colorMode() === 'light' ? space.lightLogo : space.darkLogo) ?? space.darkLogo;
  }

  select(space: Space): void {
    if (space.domain === this.spaces().active) {
      this.close();
      return;
    }
    selectSpace(space.domain);
    this.restart();
  }

  remove(space: Space): void {
    const wasActive = space.domain === this.spaces().active;
    removeSpace(space.domain);
    if (wasActive) {
      this.restart();
      return;
    }
    this.spaces.set(loadSpaces());
  }

  add(): void {
    const lookup = this.lookup();
    if (lookup.state !== 'found') return;
    addSpace(lookup.space);
    this.restart();
  }

  showForm(): void {
    this.adding.set(true);
  }

  cancelForm(): void {
    this.adding.set(false);
    this.address.setValue('', { emitEvent: false });
    this.lookup.set({ state: 'idle' });
  }

  close(): void {
    if (!this.required()) this.closed.emit();
  }

  /** Every store starts clean in the newly chosen space. */
  private restart(): void {
    this.document.defaultView?.location.reload();
  }

  /** Previews the space behind `value` from its public startup data. */
  private async lookUp(value: string): Promise<void> {
    const run = ++this.lookupRun;
    const address = parseSpaceAddress(value, config.localSpaceServer);
    if (!value.trim()) {
      this.lookup.set({ state: 'idle' });
      return;
    }
    if (!address) {
      this.lookup.set({ state: 'error', message: _('This is not a valid address.') });
      return;
    }
    this.lookup.set({ state: 'loading', domain: address.domain });
    const result = await this.fetchSpace(address);
    if (run === this.lookupRun) this.lookup.set(result);
  }

  private async fetchSpace({ domain, server }: NativeSpace): Promise<Lookup> {
    try {
      const backend = nativeBackendUrl({ domain, server });
      const response = await fetch(`${backend}/v1/startup`, {
        headers: { [TENANT_HEADER]: domain },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const startup = (await response.json()) as {
        status?: string;
        settings?: {
          title?: string;
          assets?: Record<string, { url?: string } | undefined>;
        } | null;
      };
      if (server && startup.status === 'setup-required') {
        // A new local instance: the setup wizard opens once the app
        // restarts in it.
        return {
          state: 'found',
          space: { domain, server, title: domain, darkLogo: null, lightLogo: null },
        };
      }
      if (startup.status !== 'ready' || !startup.settings) {
        return { state: 'error', message: _('This space is not set up yet.') };
      }
      // Used as given, like the app does: uploaded images are absolute,
      // the default ones point into the client bundle the app ships too.
      const asset = (key: string) => startup.settings?.assets?.[key]?.url || null;
      return {
        state: 'found',
        space: {
          domain,
          server,
          title: startup.settings.title?.trim() || domain,
          darkLogo: asset('darkLogo'),
          lightLogo: asset('lightLogo'),
        },
      };
    } catch (e) {
      console.warn('[spaces] lookup failed', { domain, server, e });
      return { state: 'error', message: _('No Rooms space found at this address.') };
    }
  }
}
